import { PageHeader } from '../components/ui/PageHeader';
import {
  useCallback,
  useEffect,
  useState,
  type FormEvent,
  type ReactNode,
} from 'react';
import { DateRangeField } from '../components/DateRangeField';
import { getErrorMessage } from '../api/client';
import { useConfirm } from '../components/ui/ConfirmDialog';
import {
  deactivateLoyverseConnection,
  getLoyverseCatalogEmployees,
  getLoyverseCatalogItems,
  getLoyverseCatalogStores,
  getLoyverseConnection,
  getLoyverseReceiptSyncStatus,
  getLoyverseSyncRuns,
  getLoyverseSyncStatus,
  saveLoyverseConnection,
  syncLoyverseCatalog,
  syncLoyverseEmployees,
  syncLoyverseItems,
  syncLoyverseReceipts,
  syncLoyverseStores,
  testLoyverseConnection,
  updateLoyverseSyncSettings,
} from '../api/loyverse';
import { AutoPostingToggle } from '../components/AutoPostingToggle';
import { ZettleConnectionCard } from '../components/ZettleConnectionCard';
import { useAuth } from '../auth/AuthProvider';
import { canManageLoyverse } from '../auth/permissions';
import { DEFAULT_STORE_TIMEZONE } from '../utils/timezones';
import type {
  LoyverseCatalogEmployee,
  LoyverseCatalogItem,
  LoyverseCatalogStore,
  LoyverseCatalogSyncSummary,
  LoyverseConnectionStatus,
  LoyverseReceiptSyncState,
  LoyverseReceiptSyncSummary,
  LoyverseSyncCounts,
  LoyverseSyncRunItem,
  LoyverseSyncState,
} from '../types/loyverse';

type CatalogOperation = 'catalog' | 'stores' | 'employees' | 'items';

type Operation =
  | 'save'
  | 'test'
  | 'deactivate'
  | CatalogOperation
  | 'receipts'
  | 'receipts-incremental'
  | 'sync-settings'
  | null;

export function LoyverseIntegrationPage() {
  const { user } = useAuth();
  const confirm = useConfirm();
  const [connection, setConnection] = useState<LoyverseConnectionStatus | null>(null);
  const [states, setStates] = useState<LoyverseSyncState[]>([]);
  const [stores, setStores] = useState<LoyverseCatalogStore[]>([]);
  const [employees, setEmployees] = useState<LoyverseCatalogEmployee[]>([]);
  const [items, setItems] = useState<LoyverseCatalogItem[]>([]);
  const [lastSummary, setLastSummary] = useState<LoyverseCatalogSyncSummary>();
  const [receiptState, setReceiptState] = useState<LoyverseReceiptSyncState>();
  const [receiptSummary, setReceiptSummary] = useState<LoyverseReceiptSyncSummary>();
  const [syncRuns, setSyncRuns] = useState<LoyverseSyncRunItem[]>([]);
  const [showSyncRuns, setShowSyncRuns] = useState(false);
  const [receiptDateFrom, setReceiptDateFrom] = useState(
    () => quickRange(3).dateFrom,
  );
  const [receiptDateTo, setReceiptDateTo] = useState(
    () => quickRange(3).dateTo,
  );
  const [accessToken, setAccessToken] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [operation, setOperation] = useState<Operation>(null);
  const [error, setError] = useState<string>();
  const [notice, setNotice] = useState<string>();
  const isOwner = canManageLoyverse(user?.role);

  const loadData = useCallback(async () => {
    setIsLoading(true);

    try {
      const [connectionResult, statusResult, storesResult, employeesResult, itemsResult, receiptStatusResult, syncRunsResult] =
        await Promise.all([
          getLoyverseConnection(),
          getLoyverseSyncStatus(),
          getLoyverseCatalogStores(),
          getLoyverseCatalogEmployees(),
          getLoyverseCatalogItems(),
          getLoyverseReceiptSyncStatus(),
          getLoyverseSyncRuns(),
        ]);
      setConnection(connectionResult.connection);
      setStates(statusResult.states);
      setStores(storesResult.stores);
      setEmployees(employeesResult.employees);
      setItems(itemsResult.items);
      setReceiptState(receiptStatusResult.state);
      setSyncRuns(syncRunsResult.items);
    } catch (loadError) {
      setError(getErrorMessage(loadError, 'No fue posible cargar la integración.'));
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadData();
  }, [loadData]);

  const handleSave = async (event: FormEvent) => {
    event.preventDefault();
    if (operation) return;

    const token = accessToken.trim();
    if (token.length < 20) {
      setError('El token Loyverse debe contener al menos 20 caracteres.');
      return;
    }

    setOperation('save');
    clearMessages();

    try {
      const result = await saveLoyverseConnection({ accessToken: token });
      setConnection(result.connection);
      setAccessToken('');
      setNotice('Token guardado de forma segura. Ya puedes probar la conexión.');
    } catch (saveError) {
      setError(getErrorMessage(saveError, 'No fue posible guardar el token.'));
    } finally {
      setOperation(null);
    }
  };

  const handleTest = async () => {
    if (operation) return;
    setOperation('test');
    clearMessages();

    try {
      const result = await testLoyverseConnection();
      setConnection(result.connection);
      setNotice('Conexión con Loyverse verificada correctamente.');
    } catch (testError) {
      setError(getErrorMessage(testError, 'No fue posible probar la conexión.'));
    } finally {
      setOperation(null);
    }
  };

  const handleDeactivate = async () => {
    if (
      operation ||
      !(await confirm({ title: 'Desactivar conexión', description: '¿Desactivar la conexión Loyverse de este negocio?', confirmLabel: 'Desactivar', danger: true }))
    ) return;

    setOperation('deactivate');
    clearMessages();

    try {
      const result = await deactivateLoyverseConnection();
      setConnection(result.connection);
      setNotice('La conexión Loyverse quedó desactivada.');
    } catch (deactivateError) {
      setError(getErrorMessage(deactivateError, 'No fue posible desactivar la conexión.'));
    } finally {
      setOperation(null);
    }
  };

  const handleSync = async (entity: CatalogOperation) => {
    if (
      operation ||
      !(await confirm({ title: 'Sincronizar catálogo', description: 'Esta acción lee datos reales desde Loyverse y los guarda en la base de esta app (Negocio360). No modifica Loyverse ni Apps Script. ¿Continuar?', confirmLabel: 'Continuar' }))
    ) return;

    setOperation(entity);
    clearMessages();

    try {
      const request = {
        catalog: syncLoyverseCatalog,
        stores: syncLoyverseStores,
        employees: syncLoyverseEmployees,
        items: syncLoyverseItems,
      }[entity];
      const result = await request();
      setLastSummary(result.summary);
      setNotice('Catálogo actualizado desde Loyverse.');
      await loadData();
    } catch (syncError) {
      setError(getErrorMessage(syncError, 'No fue posible sincronizar Loyverse.'));
      await loadData();
    } finally {
      setOperation(null);
    }
  };

  const handleReceiptSync = async () => {
    if (
      operation ||
      !(await confirm({ title: 'Sincronizar ventas', description: 'Esta acción lee ventas/tickets reales desde Loyverse y los guarda en la base de esta app (Negocio360) para el rango seleccionado. Es de piloto: no modifica Loyverse ni Apps Script, y no reemplaza la operación oficial. ¿Continuar?', confirmLabel: 'Continuar' }))
    ) return;

    setOperation('receipts');
    clearMessages();

    try {
      const result = await syncLoyverseReceipts({
        dateFrom: receiptDateFrom,
        dateTo: receiptDateTo,
      });
      setReceiptSummary(result.summary);
      setNotice('Ventas actualizadas desde Loyverse.');
      await loadData();
    } catch (syncError) {
      setError(
        getErrorMessage(syncError, 'No fue posible actualizar las ventas.'),
      );
      await loadData();
    } finally {
      setOperation(null);
    }
  };

  // Fase 21B: sync incremental — sin rango, el backend arranca desde su checkpoint (último sync
  // exitoso menos la ventana de lookback). Es el botón recomendado para el uso diario.
  const handleIncrementalSync = async () => {
    if (
      operation ||
      !(await confirm({ title: 'Sincronizar incremental', description: 'Esto leerá ventas desde Loyverse y las copiará a Negocio360, desde el último sync exitoso hasta ahora. No modifica Loyverse ni descuenta inventario. ¿Continuar?', confirmLabel: 'Continuar' }))
    ) return;

    setOperation('receipts-incremental');
    clearMessages();

    try {
      const result = await syncLoyverseReceipts({});
      setReceiptSummary(result.summary);
      setNotice('Ventas actualizadas desde Loyverse (sync incremental).');
      await loadData();
    } catch (syncError) {
      setError(getErrorMessage(syncError, 'No fue posible actualizar las ventas.'));
      await loadData();
    } finally {
      setOperation(null);
    }
  };

  // Fase 21B: activar/desactivar auto-sync — advertencia explícita antes de activar.
  const handleToggleAutoSync = async () => {
    if (operation || !receiptState) return;
    const enabling = !receiptState.autoSyncEnabled;
    if (
      enabling &&
      !(await confirm({ title: 'Activar auto-sync', description: 'Negocio360 leerá ventas de Loyverse automáticamente, solo lectura: no descuenta inventario ni cambia tus datos. Además requiere que el auto-sync esté habilitado en el servidor. ¿Activar?', confirmLabel: 'Activar' }))
    ) return;

    setOperation('sync-settings');
    clearMessages();
    try {
      const result = await updateLoyverseSyncSettings({ autoSyncEnabled: enabling });
      setNotice(result.message);
      await loadData();
    } catch (settingsError) {
      setError(getErrorMessage(settingsError, 'No fue posible actualizar la configuración de sync.'));
    } finally {
      setOperation(null);
    }
  };

  const handleIntervalChange = async (minutes: 15 | 30 | 60) => {
    if (operation) return;
    setOperation('sync-settings');
    clearMessages();
    try {
      const result = await updateLoyverseSyncSettings({ autoSyncIntervalMinutes: minutes });
      setNotice(result.message);
      await loadData();
    } catch (settingsError) {
      setError(getErrorMessage(settingsError, 'No fue posible actualizar el intervalo de sync.'));
    } finally {
      setOperation(null);
    }
  };

  const clearMessages = () => {
    setError(undefined);
    setNotice(undefined);
  };

  return (
    <section className="admin-page integration-page n3-screen">
      {/* P-075: «Integraciones» (antes «Conexión Loyverse» / «Loyverse»): aquí vive también el lector de pagos, que
          no depende de Loyverse. Solo cambia este encabezado; la parte de Loyverse no se toca. */}
      <PageHeader description="El lector de pagos con tarjeta y, si usas Loyverse, tu cuenta para traer ventas, tiendas, empleados y productos." title="Integraciones" />

      {error && <div className="alert alert--error">{error}</div>}
      {notice && <div className="alert alert--success">{notice}</div>}

      {/* PayPal/Zettle LIVE readiness (L-05): conexión OAuth con Zettle. Solo el Dueño; independiente de Loyverse. */}
      {user?.role === 'OWNER' && <ZettleConnectionCard />}

      {isLoading ? (
        <div className="admin-card inline-loading">
          <span className="spinner" />Cargando integración...
        </div>
      ) : (
        <>
          <div className="integration-grid">
            <ConnectionCard
              connection={connection}
              isOwner={isOwner}
              operation={operation}
              onTest={handleTest}
              onDeactivate={handleDeactivate}
            />
            <TokenCard
              accessToken={accessToken}
              connection={connection}
              isOwner={isOwner}
              operation={operation}
              onChange={setAccessToken}
              onSubmit={handleSave}
            />
          </div>

          <section className="admin-card integration-sync-card">
            <div className="integration-card-heading">
              <div>
                <span className="eyebrow">Productos y sucursales</span>
                <h2>Actualizar catálogo</h2>
              </div>
              {isOwner && connection?.isActive && (
                <button className="button button--primary" disabled={operation !== null} onClick={() => void handleSync('catalog')} type="button">
                  {operation === 'catalog' ? 'Actualizando...' : 'Actualizar catálogo'}
                </button>
              )}
            </div>

            <div className="sync-state-grid">
              {states.map((state) => <SyncStateCard key={state.entity} state={state} />)}
            </div>

            {isOwner && connection?.isActive && (
              <div className="integration-actions">
                <button className="button button--ghost" disabled={operation !== null} onClick={() => void handleSync('stores')} type="button">{operation === 'stores' ? 'Actualizando...' : 'Actualizar tiendas'}</button>
                <button className="button button--ghost" disabled={operation !== null} onClick={() => void handleSync('employees')} type="button">{operation === 'employees' ? 'Actualizando...' : 'Actualizar empleados'}</button>
                <button className="button button--ghost" disabled={operation !== null} onClick={() => void handleSync('items')} type="button">{operation === 'items' ? 'Actualizando...' : 'Actualizar productos'}</button>
              </div>
            )}

            {lastSummary && <SyncSummary summary={lastSummary} />}
          </section>

          <ReceiptSyncCard
            connectionActive={Boolean(connection?.isActive)}
            dateFrom={receiptDateFrom}
            dateTo={receiptDateTo}
            isOwner={isOwner}
            operation={operation}
            state={receiptState}
            summary={receiptSummary}
            onDateFromChange={setReceiptDateFrom}
            onDateToChange={setReceiptDateTo}
            onSync={handleReceiptSync}
            onIncrementalSync={handleIncrementalSync}
            onToggleAutoSync={handleToggleAutoSync}
            onIntervalChange={handleIntervalChange}
          />

          {isOwner && (
            <section className="admin-card">
              <div className="integration-card-heading">
                <div>
                  <span className="eyebrow">Auditoría</span>
                  <h2>Historial de sincronizaciones</h2>
                </div>
                <button className="button button--ghost" onClick={() => setShowSyncRuns((current) => !current)} type="button">
                  {showSyncRuns ? 'Ocultar historial' : 'Ver historial de sync'}
                </button>
              </div>
              {showSyncRuns && (syncRuns.length ? (
                <div className="data-table-wrap">
                  <table className="data-table">
                    <thead><tr><th>Fecha</th><th>Disparo</th><th>Estado</th><th>Rango</th><th>Nuevos</th><th>Actualizados</th><th>Omitidos</th><th>Duración</th><th>Error</th></tr></thead>
                    <tbody>{syncRuns.map((run) => (
                      <tr key={run.id}>
                        <td>{formatDate(run.startedAt)}</td>
                        <td>{run.trigger === 'SCHEDULED' ? 'Automático' : 'Manual'}</td>
                        <td><span className={`status-pill ${run.status === 'SUCCESS' ? '' : 'status-pill--neutral'}`}>{statusLabel(run.status)}</span></td>
                        <td><small>{formatDate(run.rangeStartAt)} – {formatDate(run.rangeEndAt)}</small></td>
                        <td>{run.receiptsCreated}</td>
                        <td>{run.receiptsUpdated}</td>
                        <td>{run.receiptsSkipped}</td>
                        <td>{run.durationMs !== null ? `${Math.round(run.durationMs / 100) / 10} s` : '—'}</td>
                        <td>{run.errorMessage ? <small>{run.errorMessage}</small> : '—'}</td>
                      </tr>
                    ))}</tbody>
                  </table>
                </div>
              ) : (
                <p className="empty-state">Aún no hay sincronizaciones registradas.</p>
              ))}
            </section>
          )}

          <CatalogSection stores={stores} employees={employees} items={items} />
        </>
      )}

      <p className="integration-scope-note">
        Actualizar ventas no modifica el inventario. El stock todavía no descuenta ventas automáticamente.
      </p>
    </section>
  );
}

interface ConnectionCardProps {
  connection: LoyverseConnectionStatus | null;
  isOwner: boolean;
  operation: Operation;
  onTest: () => Promise<void>;
  onDeactivate: () => Promise<void>;
}

function ConnectionCard({ connection, isOwner, operation, onTest, onDeactivate }: ConnectionCardProps) {
  return (
    <article className="admin-card integration-status-card">
      <div className="integration-card-heading">
        <div><span className="eyebrow">Estado actual</span><h2>Conexión Loyverse</h2></div>
        <span className={`status-pill ${connection?.isActive ? '' : 'status-pill--inactive'}`}>
          {connection?.isActive ? 'Activa' : connection?.isConfigured ? 'Inactiva' : 'Sin configurar'}
        </span>
      </div>
      <dl className="detail-list integration-details">
        <div><dt>Seguridad</dt><dd>{connection?.maskedToken ? 'Token guardado de forma segura' : 'Token no configurado'}</dd></div>
        <div><dt>Última prueba</dt><dd>{formatDate(connection?.lastTestedAt)}</dd></div>
        <div><dt>Resultado</dt><dd>{formatTestStatus(connection?.lastTestStatus)}</dd></div>
        <div><dt>Última actualización</dt><dd>{formatDate(connection?.updatedAt)}</dd></div>
      </dl>
      {connection?.lastTestMessage && <div className={`alert ${connection.lastTestStatus === 'SUCCESS' ? 'alert--success' : 'alert--warning'}`}>{connection.lastTestMessage}</div>}
      {isOwner && connection?.isActive && (
        <div className="integration-actions">
          <button className="button button--primary" disabled={operation !== null} onClick={() => void onTest()} type="button">{operation === 'test' ? 'Probando...' : 'Probar conexión'}</button>
          <button className="button button--ghost integration-danger" disabled={operation !== null} onClick={() => void onDeactivate()} type="button">{operation === 'deactivate' ? 'Desactivando...' : 'Desactivar'}</button>
        </div>
      )}
    </article>
  );
}

interface TokenCardProps {
  accessToken: string;
  connection: LoyverseConnectionStatus | null;
  isOwner: boolean;
  operation: Operation;
  onChange: (value: string) => void;
  onSubmit: (event: FormEvent) => Promise<void>;
}

function TokenCard({ accessToken, connection, isOwner, operation, onChange, onSubmit }: TokenCardProps) {
  return (
    <article className="admin-card integration-config-card">
      <span className="eyebrow">Configuración</span>
      <h2>{connection?.isConfigured ? 'Actualizar token' : 'Conectar cuenta'}</h2>
      {isOwner ? (
        <form onSubmit={(event) => void onSubmit(event)}>
          <label className="field">
            <span>Access token de Loyverse</span>
            <input autoComplete="off" disabled={operation !== null} onChange={(event) => onChange(event.target.value)} placeholder="Pega aquí el token" type="password" value={accessToken} />
          </label>
          <div className="alert alert--warning">El token se cifra antes de almacenarse y nunca se vuelve a mostrar completo.</div>
          <button className="button button--primary button--wide" disabled={operation !== null || accessToken.trim().length < 20} type="submit">{operation === 'save' ? 'Guardando...' : 'Guardar token'}</button>
        </form>
      ) : (
        <div className="empty-panel empty-panel--small"><strong>Vista de solo lectura</strong><span>Solo el Dueño puede cambiar o probar la conexión.</span></div>
      )}
    </article>
  );
}

function SyncStateCard({ state }: { state: LoyverseSyncState }) {
  return (
    <article className="sync-state-card">
      <div><strong>{entityLabel(state.entity)}</strong><span className={`status-pill ${state.status === 'SUCCESS' ? '' : 'status-pill--neutral'}`}>{statusLabel(state.status)}</span></div>
      <small>Última sincronización: {formatDate(state.lastSyncedAt)}</small>
      {state.lastError && <p>{state.lastError}</p>}
    </article>
  );
}

function SyncSummary({ summary }: { summary: LoyverseCatalogSyncSummary }) {
  return (
    <div className="sync-summary">
      <h3>Resumen de la última actualización</h3>
      <div className="sync-summary-grid">
        <SummaryCounts label="Tiendas" counts={summary.stores} />
        <SummaryCounts label="Empleados" counts={summary.employees} />
        <SummaryCounts label="Artículos" counts={summary.items} />
        <SummaryCounts label="Variantes" counts={summary.variants} />
      </div>
      {summary.warnings.length > 0 && <div className="alert alert--warning">{summary.warnings.join(' ')}</div>}
    </div>
  );
}

function SummaryCounts({ label, counts }: { label: string; counts: LoyverseSyncCounts }) {
  return <div><strong>{label}</strong><span>{counts.read} encontrados · {counts.created} nuevos · {counts.updated} actualizados · {counts.skipped} sin cambios</span></div>;
}

interface ReceiptSyncCardProps {
  connectionActive: boolean;
  dateFrom: string;
  dateTo: string;
  isOwner: boolean;
  operation: Operation;
  state?: LoyverseReceiptSyncState;
  summary?: LoyverseReceiptSyncSummary;
  onDateFromChange: (value: string) => void;
  onDateToChange: (value: string) => void;
  onSync: () => Promise<void>;
  onIncrementalSync: () => Promise<void>;
  onToggleAutoSync: () => Promise<void>;
  onIntervalChange: (minutes: 15 | 30 | 60) => Promise<void>;
}

function ReceiptSyncCard({
  connectionActive,
  dateFrom,
  dateTo,
  isOwner,
  operation,
  state,
  summary,
  onDateFromChange,
  onDateToChange,
  onSync,
  onIncrementalSync,
  onToggleAutoSync,
  onIntervalChange,
}: ReceiptSyncCardProps) {
  const canSync =
    isOwner &&
    connectionActive &&
    Boolean(dateFrom) &&
    Boolean(dateTo) &&
    operation === null;

  return (
    <section className="admin-card receipt-sync-card">
      <div className="integration-card-heading">
        <div>
          <span className="eyebrow">Tickets y ventas</span>
          <h2>Actualizar ventas desde Loyverse</h2>
        </div>
        <span className={`status-pill ${state?.status === 'SUCCESS' ? '' : 'status-pill--neutral'}`}>
          {statusLabel(state?.status || 'IDLE')}
        </span>
      </div>

      <div className="receipt-sync-layout">
        <div>
          <div className="receipt-date-grid">
            <DateRangeField allowClear={false} disabled={!isOwner || operation !== null} from={dateFrom} maxToday onChange={(from, to) => { onDateFromChange(from); onDateToChange(to); }} to={dateTo} />
          </div>

          {isOwner ? (
            <>
              {/* Fase 21B: sync incremental — el botón recomendado para el uso diario, sin elegir fechas. */}
              <button className="button button--primary" disabled={!isOwner || !connectionActive || operation !== null} onClick={() => void onIncrementalSync()} type="button">
                {operation === 'receipts-incremental' ? 'Sincronizando...' : 'Sincronizar ventas ahora'}
              </button>
              {/* Ola 4 (D8): los atajos de fecha viven DENTRO del calendario del rango; se retiran
                  los botones duplicados de "Últimos N días". */}
              <small className="integration-scope-note">Trae lo nuevo desde el último sync exitoso. Usa las fechas de abajo solo para re-sincronizar días específicos.</small>
              <div className="alert alert--warning">La actualización puede tardar algunos segundos. El rango máximo es de 7 días.</div>
              <button className="button button--secondary" disabled={!canSync} onClick={() => void onSync()} type="button">
                {operation === 'receipts' ? 'Actualizando ventas...' : 'Actualizar rango elegido'}
              </button>
            </>
          ) : (
            <div className="empty-panel empty-panel--small">
              <strong>Vista de solo lectura</strong>
              <span>Solo el Dueño puede actualizar ventas.</span>
            </div>
          )}
        </div>

        <div className="receipt-sync-status">
          <small>Última sincronización exitosa: {formatDate(state?.lastSyncedAt)}</small>
          <small>Último intento: {formatDate(state?.lastAttemptAt)}</small>
          {/* Fase 21B: configuración del sync automático — apagado por default, con doble candado
              (toggle por negocio + ENABLE_LOYVERSE_AUTO_SYNC en el servidor). */}
          {isOwner && (
            <div className="receipt-autosync-settings">
              <small>
                Sync automático: <strong>{state?.autoSyncEnabled ? `Activado (cada ${state.autoSyncIntervalMinutes} min)` : 'Desactivado'}</strong>
              </small>
              <div className="receipt-quick-actions">
                <button className="button button--ghost" disabled={operation !== null} onClick={() => void onToggleAutoSync()} type="button">
                  {state?.autoSyncEnabled ? 'Desactivar sync automático' : 'Configurar sync automático'}
                </button>
                {state?.autoSyncEnabled && (
                  <>
                    <button className="button button--ghost" disabled={operation !== null || state.autoSyncIntervalMinutes === 15} onClick={() => void onIntervalChange(15)} type="button">Cada 15 min</button>
                    <button className="button button--ghost" disabled={operation !== null || state.autoSyncIntervalMinutes === 30} onClick={() => void onIntervalChange(30)} type="button">Cada 30 min</button>
                    <button className="button button--ghost" disabled={operation !== null || state.autoSyncIntervalMinutes === 60} onClick={() => void onIntervalChange(60)} type="button">Cada 60 min</button>
                  </>
                )}
              </div>
              {/* FI-19: qué pasa DESPUÉS de sincronizar. Va aquí y no en Descuento por ventas porque
                  es la otra mitad de la misma pregunta —"¿qué corre solo, y cada cuánto?"— y porque
                  una vez encendido, la pantalla del descuento es justo la que dejas de abrir. */}
              <AutoPostingToggle />
            </div>
          )}
          {state?.lastError && <div className="alert alert--error">La última actualización terminó con un error. Intenta nuevamente.</div>}
          {summary ? (
            <div className="receipt-summary-grid">
              <div><strong>{summary.receiptsRead}</strong><span>Tickets encontrados</span></div>
              <div><strong>{summary.receiptsCreated}</strong><span>Tickets nuevos</span></div>
              <div><strong>{summary.receiptsUpdated}</strong><span>Tickets actualizados</span></div>
              <div><strong>{summary.linesCreated + summary.linesUpdated}</strong><span>Productos procesados</span></div>
              <div><strong>{summary.paymentsCreated + summary.paymentsUpdated}</strong><span>Pagos procesados</span></div>
              <div><strong>{summary.pages}</strong><span>Bloques consultados</span></div>
            </div>
          ) : (
            <p className="empty-state">Aún no hay resumen en esta sesión.</p>
          )}
          {summary && <small>Duración: {summary.durationMs} ms · Rango: {summary.dateFrom.slice(0, 10)} a {summary.dateTo.slice(0, 10)}</small>}
          {summary?.warnings.length ? <div className="alert alert--warning">{summary.warnings.join(' ')}</div> : null}
          <button className="text-button" onClick={() => { window.location.hash = 'sales'; }} type="button">Ver ventas sincronizadas →</button>
        </div>
      </div>
    </section>
  );
}

function CatalogSection({ stores, employees, items }: { stores: LoyverseCatalogStore[]; employees: LoyverseCatalogEmployee[]; items: LoyverseCatalogItem[] }) {
  return (
    <section className="catalog-grid">
      <CatalogCard title="Tiendas detectadas" count={stores.length}>{stores.slice(0, 20).map((store) => <CatalogRow key={store.id} title={store.name} subtitle={[store.city, store.region].filter(Boolean).join(', ') || 'Sin ubicación'} active={store.isActive} />)}</CatalogCard>
      <CatalogCard title="Empleados detectados" count={employees.length}>{employees.slice(0, 20).map((employee) => <CatalogRow key={employee.id} title={employee.name} subtitle={employee.email || employee.role || 'Sin correo'} active={employee.isActive} />)}</CatalogCard>
      <CatalogCard title="Productos detectados" count={items.length}>{items.slice(0, 20).map((item) => <CatalogRow key={item.id} title={item.name} subtitle={`${item.variants.length} variante${item.variants.length === 1 ? '' : 's'}`} active={item.isActive} />)}</CatalogCard>
    </section>
  );
}

function CatalogCard({ title, count, children }: { title: string; count: number; children: ReactNode }) {
  return (
    <article className="admin-card catalog-card">
      <div className="catalog-card-heading"><h2>{title}</h2><span>{count}</span></div>
      <div className="catalog-list">{count ? children : <p className="empty-state">Sin registros sincronizados.</p>}</div>
      {count > 20 && <small className="catalog-more">Mostrando 20 de {count}.</small>}
    </article>
  );
}

function CatalogRow({ title, subtitle, active }: { title: string; subtitle: string; active: boolean }) {
  return <div className={`catalog-row ${active ? '' : 'catalog-row--inactive'}`}><span>{title.slice(0, 1).toUpperCase()}</span><div><strong>{title}</strong><small>{subtitle}</small></div></div>;
}

function formatDate(value: string | null | undefined): string {
  return value ? new Intl.DateTimeFormat('es-MX', { dateStyle: 'medium', timeStyle: 'short', timeZone: DEFAULT_STORE_TIMEZONE }).format(new Date(value)) : 'Sin registro';
}

function formatTestStatus(value: string | null | undefined): string {
  if (value === 'SUCCESS') return 'Conexión válida';
  if (value === 'FAILED') return 'Prueba fallida';
  return 'Sin probar';
}

function entityLabel(entity: LoyverseSyncState['entity']): string {
  return { STORES: 'Tiendas', EMPLOYEES: 'Empleados', ITEMS: 'Artículos' }[entity];
}

function statusLabel(status: string): string {
  return { IDLE: 'Pendiente', RUNNING: 'En proceso', SUCCESS: 'Correcto', FAILED: 'Fallido' }[status] || status;
}

function quickRange(days: number): { dateFrom: string; dateTo: string } {
  const dateTo = new Date();
  const dateFrom = new Date(dateTo);
  dateFrom.setDate(dateFrom.getDate() - Math.max(0, days - 1));
  return { dateFrom: toDateInput(dateFrom), dateTo: toDateInput(dateTo) };
}


function toDateInput(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}
